/**
 * 金币系统 - 管理金币掉落、收集和显示
 */

import * as THREE from 'three';

export class CoinSystem {
    constructor(game) {
        this.game = game;
        this.coins = new Map();
        this.playerCoins = 0; // 当前实时金币
        this.totalEarnedCoins = 0; // 累计获取的总金币（不受升级或死亡扣除影响）

        // 金币配置
        this.coinLifetime = 30000; // 30秒后消失
        this.magnetRange = 10; // 自动吸附范围，增大一点
        this.collectRange = 5; // 收集范围，增大一点方便收集
        this.coinTilt = 0.45; // 更面向玩家的俯仰（弧度，约26°）
        this.coinFloatY = 1.4; // 完全浮在水面之上
        this.coinRadius = 1.2; // 硬币半径
        this.coinThickness = 0.28; // 再厚一点
        this.magnetBaseSpeed = 26; // 磁吸基础速度（保证高于船速度）

        // 动画相关（基于帧的聚合窗口，避免重复触发/翻倍）
        this.burstWindowMs = 220; // 聚合窗口时长（ms）
        this.burstActive = false;
        this.burstStartTime = 0;
        this.burstCount = 0;
        this.burstValue = 0;
        this.currentBatchId = 0; // 每次掉落的一组硬币的批次ID
        this.activeBatchId = null; // 当前聚合窗口对应的批次ID
    }

    // 敌船被击沉时掉落金币
    dropCoins(position, totalValue = 5, options = {}) {
        const batchId = ++this.currentBatchId;
        const value = Math.max(1, Math.round(totalValue));
        const coinPosition = new THREE.Vector3(
            position.x,
            this.coinFloatY,
            position.z
        );

        return this.createCoin(coinPosition, batchId, value, options);
    }

    createCoin(position, batchId = null, value = 1, options = {}) {
        // 使用分组：父组与物理体同步，子网格用于视觉和自转，避免被物理同步重置朝向
        const coinGroup = new THREE.Group();
        coinGroup.position.copy(position);

        const collectorType = options.collectorType || 'player';
        const ownerId = options.ownerId || null;
        const ownerFaction = options.ownerFaction || null;

        // 创建立体金币外观 - 使用更薄的圆柱体
        const coinGeometry = new THREE.CylinderGeometry(this.coinRadius, this.coinRadius, this.coinThickness, 24);

        // 创建金币材质（恢复亮金黄的Phong质感）
        const coinMaterial = new THREE.MeshPhongMaterial({
            color: 0xFFD700, // 亮金黄
            emissive: 0xCC8800, // 金色自发光提亮
            emissiveIntensity: 0.7,
            specular: 0xFFF2A0, // 高光偏暖
            shininess: 300,
        });

        const coinVisual = new THREE.Mesh(coinGeometry, coinMaterial);
        // Coins are small and numerous; disable dynamic shadows for performance
        coinVisual.castShadow = false;
        coinVisual.receiveShadow = false;

        // 将金币放在 XY 平面上（圆面朝上/朝下），绕 Z 轴旋转
        // Cylinder 默认沿 Y 轴，圆面在 XZ 平面；需绕 X 轴旋转 90° 使圆面到 XY 平面
        coinVisual.rotation.x = Math.PI / 2 - this.coinTilt; // 略微朝向玩家

        // 添加金币中央的符号装饰
        // 中央浅浮雕（更薄，不外凸）
        const symbolGeometry = new THREE.CylinderGeometry(this.coinRadius * 0.7, this.coinRadius * 0.7, this.coinThickness * 0.7, 24);
        const symbolMaterial = new THREE.MeshPhongMaterial({
            color: 0xFFCC33, // 更亮的金黄
            emissive: 0xAA5500,
            emissiveIntensity: 0.8,
            specular: 0xFFF2A0,
            shininess: 320,
        });
        const symbolMesh = new THREE.Mesh(symbolGeometry, symbolMaterial);
        coinVisual.add(symbolMesh);

        // 去掉外围的灰色圆环装饰，避免影响视觉

        // 移除外部光环（根据反馈暂不使用）

        // 把视觉网格加入分组
        coinGroup.add(coinVisual);

        // 创建物理体（用于位置管理）
        const body = this.game.physicsSystem.createProjectileBody(position, 0.8);
        body.collisionFilterGroup = 16; // 金币碰撞组
        body.collisionFilterMask = 0; // 不需要物理碰撞，用距离检测收集

        // 设置为静态，不受重力影响
        body.type = 1; // CANNON.Body.KINEMATIC
        body.velocity.set(0, 0, 0); // 静止状态
        body.material.friction = 0;
        body.material.restitution = 0;

        // 添加用户数据
        body.userData = {
            type: 'coin',
            coinObject: null
        };

        // 创建金币对象
        const coinId = `coin_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
        const coinObject = {
            id: coinId,
            mesh: coinGroup,
            body: body,
            lifeTime: 0,
            maxLifeTime: this.coinLifetime,
            isCollected: false,
            rotationSpeed: 2 + Math.random() * 3, // 更慢的摆动速度，便于从上视角观看
            rotationPhase: Math.random() * Math.PI * 2,
            bobPhase: Math.random() * Math.PI * 2,
            glowPhase: Math.random() * Math.PI * 2,
            magnetEffect: false,
            batchId: batchId,
            value: Math.max(1, Math.round(value)),
            collectorType: collectorType,
            ownerId: ownerId,
            ownerFaction: ownerFaction,

            update: (deltaTime) => {
                if (coinObject.isCollected) return;

                // 同步父组和物理体
                if (coinGroup && body) {
                    this.game.physicsSystem.constructor.syncMeshWithBody(coinGroup, body);
                }

                // 绕 Z 轴旋转（硬币放在 XY 平面上）
                coinObject.rotationPhase += deltaTime * coinObject.rotationSpeed;
                coinVisual.rotation.z = coinObject.rotationPhase;

                // 轻微上下浮动（只移动视觉，不动物理体），始终在水面之上
                coinObject.bobPhase += deltaTime * 1.6;
                const bobOffset = Math.sin(coinObject.bobPhase) * 0.12; // ±0.12
                coinVisual.position.y = bobOffset;

                // 自发光闪烁（恢复更亮的呼吸感）
                coinObject.glowPhase += deltaTime * (1.0 + (coinObject.rotationSpeed - 2) * 0.2);
                if (coinMaterial.emissiveIntensity !== undefined) {
                    coinMaterial.emissiveIntensity = 0.65 + Math.sin(coinObject.glowPhase * 1.2) * 0.18;
                }

                // 取消光晕动画

                // 更新生命周期
                coinObject.lifeTime += deltaTime * 1000;

                // 检查拾取者距离
                if (coinObject.collectorType === 'player') {
                    this.checkPlayerProximity(coinObject);
                } else if (coinObject.collectorType === 'enemy') {
                    this.checkEnemyProximity(coinObject);
                } else if (coinObject.collectorType === 'shared') {
                    this.checkPlayerProximity(coinObject);
                    this.checkEnemyProximity(coinObject);
                }

                // 生命周期结束或落到地面以下
                if (coinObject.lifeTime > coinObject.maxLifeTime || body.position.y < -5) {
                    coinObject.shouldRemove = true;
                }
            },

            dispose: () => {
                if (coinGroup) {
                    coinGroup.traverse((child) => {
                        if (child.geometry) child.geometry.dispose();
                        if (child.material) {
                            if (Array.isArray(child.material)) {
                                child.material.forEach(mat => mat.dispose());
                            } else {
                                child.material.dispose();
                            }
                        }
                    });
                }
            }
        };

        // 设置相互引用
        body.userData.coinObject = coinObject;

        // 添加到游戏
        this.coins.set(coinId, coinObject);
        this.game.addGameObject(coinId, coinObject);

        return coinObject;
    }

    // 检查玩家接近度，实现磁铁效果
    checkPlayerProximity(coinObject) {
        if (!this.game.player || !this.game.player.body || coinObject.isCollected) return;

        const playerPos = new THREE.Vector3().copy(this.game.player.body.position);
        const coinPos = new THREE.Vector3().copy(coinObject.body.position);
        const distance = playerPos.distanceTo(coinPos);


        // 磁铁效果
        if (distance <= this.magnetRange && !coinObject.magnetEffect) {
            coinObject.magnetEffect = true;
        }

        if (coinObject.magnetEffect) {
            // 直接移动向玩家位置（速度高于船体，随距离略增）
            const direction = new THREE.Vector3().subVectors(playerPos, coinPos).normalize();
            const dt = (this.game && this.game.deltaTime) ? this.game.deltaTime : (1/60);
            const playerSpeed = (this.game && this.game.player && this.game.player.maxVelocity) ? this.game.player.maxVelocity : 15;
            let magnetSpeed = Math.max(this.magnetBaseSpeed, playerSpeed * 1.6); // 至少为船速的1.6倍
            magnetSpeed += Math.min(20, distance * 0.6); // 距离越远越快，最多+20
            magnetSpeed = Math.min(magnetSpeed, 70); // 上限

            // 直接设置位置，而不是速度
            coinObject.body.position.x += direction.x * magnetSpeed * dt;
            coinObject.body.position.z += direction.z * magnetSpeed * dt;
            coinObject.body.position.y = this.coinFloatY; // 保持完全浮在水面上
        }

        // 收集检测
        if (distance <= this.collectRange) {
            this.collectCoin(coinObject, this.game.player);
        }
    }

    checkEnemyProximity(coinObject) {
        if (coinObject.isCollected) return;
        const enemies = this.game?.enemyManager?.enemies;
        if (!enemies || typeof enemies.get !== 'function') return;

        const ownerId = coinObject.ownerId;
        if (!ownerId) return;
        const enemy = enemies.get(ownerId);
        if (!enemy || !enemy.body) return;

        const enemyPos = new THREE.Vector3().copy(enemy.body.position);
        const coinPos = new THREE.Vector3().copy(coinObject.body.position);
        const distance = enemyPos.distanceTo(coinPos);

        if (distance <= this.collectRange) {
            this.collectCoin(coinObject, enemy);
        }
    }

    // 收集金币
    collectCoin(coinObject, collector = null) {
        if (coinObject.isCollected) return;

        coinObject.isCollected = true;
        const coinValue = coinObject.value || 1;
        const collectorFaction = collector && collector.faction ? collector.faction : (coinObject.collectorType === 'enemy' ? 'enemy' : 'player');

        if (collectorFaction === 'player') {
            this.playerCoins += coinValue;
            this.totalEarnedCoins += coinValue; // 累计总金币

            const now = performance.now ? performance.now() : Date.now();
            const coinBatch = coinObject.batchId ?? 0;
            if (!this.burstActive) {
                this.burstActive = true;
                this.activeBatchId = coinBatch;
                this.burstStartTime = now;
                this.burstCount = 1;
                this.burstValue = coinValue;
            } else if (this.activeBatchId !== coinBatch) {
                if (this.burstCount > 0) {
                    this.triggerCoinAnimation(this.burstValue);
                }
                this.activeBatchId = coinBatch;
                this.burstStartTime = now;
                this.burstCount = 1;
                this.burstValue = coinValue;
            } else {
                this.burstCount += 1;
                this.burstValue += coinValue;
            }
        } else if (collectorFaction === 'enemy' && collector) {
            collector.collectedCoins = (collector.collectedCoins || 0) + coinValue;

            // Update enemy slot in real-time
            if (this.game?.rankingSystem && collector.baseName) {
                this.game.rankingSystem.saveEnemyGold(collector.baseName, collector.collectedCoins);
            }
        }

        this.createCollectEffect(coinObject.mesh.position);

        coinObject.shouldRemove = true;
        this.coins.delete(coinObject.id);

        if (collectorFaction === 'player') {
            if (this.game.rankingUI) {
                this.game.rankingUI.updateCoinDisplay(this.playerCoins);
            }
            if (this.game && this.game.mobileStatsOverlay) {
                this.game.mobileStatsOverlay.update();
            }
            if (this.game.player && typeof this.game.player.checkUpgrade === 'function') {
                this.game.player.checkUpgrade();
            }
        }
    }

    // 创建收集特效
    createCollectEffect(position) {
        // 金色粒子效果
        for (let i = 0; i < 8; i++) {
            const particleGeometry = new THREE.SphereGeometry(0.1);
            const particleMaterial = new THREE.MeshBasicMaterial({
                color: 0xFFD700,
                transparent: true,
                opacity: 1
            });

            const particle = new THREE.Mesh(particleGeometry, particleMaterial);
            particle.position.copy(position);

            this.game.sceneManager.add(particle);

            // 粒子动画
            const velocity = new THREE.Vector3(
                (Math.random() - 0.5) * 20,
                Math.random() * 15 + 5,
                (Math.random() - 0.5) * 20
            );

            let life = 1.0;
            const animateParticle = () => {
                life -= 0.05;

                particle.position.x += velocity.x * 0.02;
                particle.position.y += velocity.y * 0.02;
                particle.position.z += velocity.z * 0.02;
                velocity.y -= 0.5; // 重力

                particleMaterial.opacity = life;
                particle.scale.setScalar(life);

                if (life > 0) {
                    requestAnimationFrame(animateParticle);
                } else {
                    this.game.sceneManager.remove(particle);
                    particleGeometry.dispose();
                    particleMaterial.dispose();
                }
            };

            animateParticle();
        }
    }

    update(deltaTime) {
        // 触发聚合窗口动画（到时一次性触发）
        if (this.burstActive) {
            const now = performance.now ? performance.now() : Date.now();
            if (now - this.burstStartTime >= this.burstWindowMs) {
                const count = this.burstCount;
                if (count > 0) {
                    // 使用玩家当前位置
                    this.triggerCoinAnimation(this.burstValue);
                }
                this.burstActive = false;
                this.burstCount = 0;
                this.burstValue = 0;
                this.activeBatchId = null;
            }
        }
        // 清理已收集或过期的金币
        const coinsToRemove = [];
        this.coins.forEach((coin, id) => {
            if (coin.shouldRemove) {
                coinsToRemove.push(id);
            }
        });

        coinsToRemove.forEach(id => {
            this.coins.delete(id);
            this.game.removeGameObject(id);
        });
    }

    // 获取玩家金币数（实时金币）
    getPlayerCoins() {
        return this.playerCoins;
    }

    // 获取玩家累计获取的总金币（不受升级或死亡扣除影响）
    getTotalEarnedCoins() {
        return this.totalEarnedCoins;
    }

    // 花费金币
    spendCoins(amount) {
        if (this.playerCoins >= amount) {
            this.playerCoins -= amount;
            if (this.game && this.game.rankingUI) {
                this.game.rankingUI.updateCoinDisplay(this.playerCoins);
            }
            if (this.game && this.game.mobileStatsOverlay) {
                this.game.mobileStatsOverlay.update();
            }
            return true;
        }
        return false;
    }

    // 扣除金币（用于死亡惩罚等场景）
    loseCoins(amount) {
        const deduction = Math.max(0, Math.round(amount));
        if (deduction <= 0) {
            return 0;
        }

        const actualDeduction = Math.min(this.playerCoins, deduction);
        if (actualDeduction <= 0) {
            return 0;
        }

        this.playerCoins -= actualDeduction;

        if (this.game && this.game.rankingUI) {
            this.game.rankingUI.updateCoinDisplay(this.playerCoins);
        }

        if (this.game && this.game.mobileStatsOverlay) {
            this.game.mobileStatsOverlay.update();
        }

        return actualDeduction;
    }

    // 触发金币动画
    triggerCoinAnimation(coinValue) {
        if (this.game.player && this.game.coinAnimationUI && coinValue > 0) {
            // 获取玩家船只位置
            const playerPosition = this.game.player.mesh ?
                this.game.player.mesh.position :
                new THREE.Vector3(0, 0, 0);

            // 触发动画
            this.game.coinAnimationUI.createCoinAnimation(playerPosition, coinValue);
        }
    }

    // 清理所有金币
    dispose() {
        this.burstActive = false;
        this.burstCount = 0;
        this.activeBatchId = null;
        this.coins.forEach(coin => {
            if (coin.dispose) coin.dispose();
        });
        this.coins.clear();
    }

    // Clear all coins (for game restart)
    clearCoins() {
        this.burstActive = false;
        this.burstCount = 0;
        this.activeBatchId = null;
        this.coins.forEach((coin, id) => {
            if (coin.dispose) coin.dispose();
            this.game.removeGameObject(id);
        });
        this.coins.clear();
        this.playerCoins = 0;
    }
}
